import moment from 'moment';
import { PropTypes } from 'prop-types';
import DataTable from '../../../core/components/DataTable';
import PageActions from '../../../core/components/PageActions';
import TableActionButtonsCell from '../../../core/components/TableActionButtonsCell';
import { PermissionKeys } from '../../../core/components/UserPermissions';
import { formatAmount } from '../../../utils/money-util';
import { formatNumber } from '../../../utils/number-util';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import {
  addPermissionRefs,
  viewPermissionRefs,
} from '../../../core/components/UserGroupPermissions';
import { get, map } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { hasRole } from '../../../core/components/Permission';
import { selectPermissions, selectRole, selectSite } from '../../../store/authReducer';
import VisualComponent from '../../overview/components/VisualComponent';
import { selectDashboard } from '../../analytics/reducer';
import { utilityVisualIds } from '../chartTypes';
import UserGroupPermissions from '../../../core/components/UserGroupPermissions';
import { showDialog } from '../../../core/actions';
import { useGetUtilitiesQuery, useDeleteUtilityMutation } from '../api';

// Constants
const NAME_MAX_LENGTH = 25;
const NAME_TRUNCATE_LENGTH = 22;
const OPERATOR_ADMIN_ROLE = 'OperatorAdmin';

// Utility type to message ID mapping
const UTILITY_TYPE_MESSAGES = {
  electric: { id: 'addElectricBill', default: 'Add Electric Bill' },
  gas: { id: 'addGasBill', default: 'Add Gas Bill' },
  water: { id: 'addWaterSewerBill', default: 'Add Water/Sewer Bill' },
  sewer: { id: 'addWaterSewerBill', default: 'Add Water/Sewer Bill' },
};

export const UtilityTable = ({ pageFilter }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [sortQuery, setSortQuery] = useState(null);
  const intl = useIntl();
  const dispatch = useDispatch();
  const siteId = useSelector(selectSite);
  const role = useSelector(selectRole);
  const permissions = useSelector(selectPermissions);

  const { data, isFetching } = useGetUtilitiesQuery({
    siteId,
    types: pageFilter || '',
    page: currentPage,
  });

  const utilities = data?.results ?? [];
  const paginationData = {
    total: data?.total ?? 0,
    page: data?.page ?? 0,
    pageSize: data?.pageSize ?? 20,
  };

  const [deleteUtilityMutation] = useDeleteUtilityMutation();

  // Reset page when tab changes
  useEffect(() => {
    setCurrentPage(0);
  }, [pageFilter]);

  // Get utility type message configuration
  const getUtilityTypeMessage = useCallback(() => {
    const filterLower = String(pageFilter || '').toLowerCase();
    const type = Object.keys(UTILITY_TYPE_MESSAGES).find((key) => filterLower.includes(key));
    return type
      ? UTILITY_TYPE_MESSAGES[type]
      : { id: 'addUtilityBill', default: 'Add Utility Bill' };
  }, [pageFilter]);

  // Get page actions with memoization
  const pageActions = useMemo(() => {
    const { id: addBillMessageId, default: addBillDefault } = getUtilityTypeMessage();
    return [
      {
        dialogType: 'UPSERT_UTILITY',
        permissionKey: addPermissionRefs.expense,
        text: intl.formatMessage({ id: addBillMessageId, defaultMessage: addBillDefault }),
        dialogProps: {
          isNew: true,
          title: intl.formatMessage({ id: addBillMessageId, defaultMessage: addBillDefault }),
          tabType: pageFilter,
        },
      },
    ];
  }, [getUtilityTypeMessage, intl, pageFilter]);

  // Check if user has admin role
  const isOperatorAdmin = useMemo(() => hasRole(role, OPERATOR_ADMIN_ROLE), [role]);

  // Check permissions
  const canDeleteUtils = useMemo(
    () => permissions[PermissionKeys.deleteUtils] || isOperatorAdmin,
    [permissions, isOperatorAdmin]
  );

  const canAddEditUtils = useMemo(
    () => permissions[PermissionKeys.addEditUtils] || isOperatorAdmin,
    [permissions, isOperatorAdmin]
  );

  const hasAnyActionPermission = useMemo(
    () => canDeleteUtils || canAddEditUtils,
    [canDeleteUtils, canAddEditUtils]
  );

  // Edit utility action — pass utilityId; dialog fetches internally
  const editUtilityAction = useCallback(
    (event, { _id }) => {
      dispatch(
        showDialog('UPSERT_UTILITY', {
          title: intl.formatMessage({ id: 'editUtility' }),
          isNew: false,
          utilityId: _id,
          tabType: pageFilter,
        })
      );
    },
    [dispatch, intl, pageFilter]
  );

  // Delete utility action — invalidatesTags handles list refresh
  const deleteUtilityAction = useCallback(
    (event, { _id, name }) => {
      const confirmProps = {
        onReady: () => deleteUtilityMutation({ id: _id }),
        text: intl.formatMessage({ id: 'archiveUtilityConfirmation' }, { name }),
        title: intl.formatMessage({ id: 'archiveUtility' }),
      };
      dispatch(showDialog('CONFIRM_DIALOG', confirmProps));
    },
    [deleteUtilityMutation, dispatch, intl]
  );

  // Calculate final usage from API or readings
  const calculateFinalUsage = useCallback((utility) => {
    const usageFromApi = Number(utility?.usage);
    const usageFromReadings = Number(utility?.endReading) - Number(utility?.startReading);

    if (Number.isFinite(usageFromApi) && usageFromApi > 0) {
      return usageFromApi;
    }
    if (Number.isFinite(usageFromReadings) && usageFromReadings > 0) {
      return usageFromReadings;
    }
    return null;
  }, []);

  // Calculate cost per unit
  const calculateCostPerUnit = useCallback((utility, finalUsage) => {
    const costNum = Number(utility?.cost);
    const hasValidCpu = Number.isFinite(finalUsage) && finalUsage > 0 && Number.isFinite(costNum);

    if (!hasValidCpu) {
      return '-';
    }

    const costPerUnitValue = formatAmount(costNum / finalUsage, utility.currency, true);
    const unitSuffix = utility?.unit ? ` / ${utility.unit}` : '';
    return `${costPerUnitValue}${unitSuffix}`;
  }, []);

  const formatCostPerCar = useCallback((utility) => {
    if (utility?.costPerCar == null) {
      return '-';
    }

    const costPerCar = Number(utility.costPerCar);
    if (!Number.isFinite(costPerCar)) {
      return '-';
    }
    return formatAmount(costPerCar, utility.currency, true);
  }, []);

  // Format display name with truncation
  const formatDisplayName = useCallback((name) => {
    if (typeof name === 'string' && name.length > NAME_MAX_LENGTH) {
      return `${name.slice(0, NAME_TRUNCATE_LENGTH)}...`;
    }
    return name;
  }, []);

  // Transform utility data for table
  const getTableData = useMemo(() => {
    return map(utilities, (utility) => {
      const finalUsage = calculateFinalUsage(utility);
      const costPerUnit = calculateCostPerUnit(utility, finalUsage);

      return {
        _id: utility.id,
        name: formatDisplayName(utility.name),
        start: moment(utility.start).format('MM/DD/YYYY'),
        end: moment(utility.end).format('MM/DD/YYYY'),
        startReading: formatNumber(utility.startReading, 4, true),
        endReading: formatNumber(utility.endReading, 4, true),
        cost: formatAmount(utility.cost, utility.currency, true),
        costPerUnit,
        costPerCar: formatCostPerCar(utility),
        // sewerCostPerGallon,
      };
    });
  }, [utilities, calculateFinalUsage, calculateCostPerUnit, formatCostPerCar, formatDisplayName]);

  // Handle sorting
  const triggerUtilities = useCallback((sortedQuery) => {
    if (get(sortedQuery, 'key', false)) {
      setSortQuery(sortedQuery);
    }
  }, []);

  // Initialize action button header
  const initializeActionButtonHeader = useMemo(() => {
    const buttons = [];

    if (canDeleteUtils) {
      buttons.push({
        icon: 'icon-trash',
        buttonAction: deleteUtilityAction,
      });
    }
    if (canAddEditUtils) {
      buttons.push({
        icon: 'icon-edit',
        buttonAction: editUtilityAction,
      });
    }

    return {
      name: intl.formatMessage({ id: 'action' }),
      cell: {
        component: TableActionButtonsCell,
        mixIns: {
          buttons,
        },
      },
    };
  }, [intl, canDeleteUtils, canAddEditUtils, deleteUtilityAction, editUtilityAction]);

  // Initialize datatable configuration
  const initializeDatatable = useMemo(() => {
    const headers = [
      { name: intl.formatMessage({ id: 'billDescription' }), databaseProperty: 'name' },
      { name: intl.formatMessage({ id: 'startDate' }), databaseProperty: 'start' },
      { name: intl.formatMessage({ id: 'endDate' }), databaseProperty: 'end' },
      { name: intl.formatMessage({ id: 'startReading' }), databaseProperty: 'startReading' },
      { name: intl.formatMessage({ id: 'endReading' }), databaseProperty: 'endReading' },
      { name: intl.formatMessage({ id: 'amount' }), databaseProperty: 'cost' },
      {
        name: intl.formatMessage({ id: 'costPerUnit', defaultMessage: 'Cost / Unit' }),
        databaseProperty: 'costPerUnit',
      },
      {
        name: intl.formatMessage({ id: 'utilityCostPerCar', defaultMessage: 'Cost / Car' }),
        databaseProperty: 'costPerCar',
      },
    ];

    if (hasAnyActionPermission) {
      headers.push(initializeActionButtonHeader);
    }

    const paginationInfo = {
      ...paginationData,
      goToPage: setCurrentPage,
    };

    const getSubRows = () => {
      return (utilities || [])
        .filter((u) => Array.isArray(u.lineItems) && u.lineItems.length > 0)
        .map((u) => {
          const parentRow = u.id;
          const rows = {
            data: u.lineItems.map((li, idx) => {
              const start = li.startReading ? moment(u.start).format('MM/DD/YYYY') : '-';
              const end = li.endReading ? moment(u.end).format('MM/DD/YYYY') : '-';
              const cost = formatAmount(li.cost, u.currency, true);
              const liStart = Number(li.startReading);
              const liEnd = Number(li.endReading);
              const liCostNum = Number(li.cost);
              const liUsage = liEnd - liStart;
              const hasValidCpu =
                Number.isFinite(liUsage) && liUsage > 0 && Number.isFinite(liCostNum);
              const costPerUnit = hasValidCpu
                ? `${formatAmount(liCostNum / liUsage, u.currency, true)}${
                    u?.unit ? ` / ${u.unit}` : ''
                  }`
                : '-';
              const typeText =
                typeof li?.type === 'string'
                  ? li.type
                  : typeof li?.type === 'number'
                  ? ['Water', 'Electric', 'Gas', 'Sewer'][li.type] || ''
                  : '';
              const displayNameBase = li.name || li.description || 'Line item';
              const displayName = typeText
                ? `${displayNameBase} — ${intl.formatMessage({ id: 'type' })}: ${typeText}`
                : displayNameBase;
              return {
                _id: `${parentRow}-li-${idx}`,
                name: displayName,
                start,
                end,
                startReading: formatNumber(li.startReading, 2, true) || '-',
                endReading: formatNumber(li.endReading, 2, true) || '-',
                cost,
                costPerUnit,
                costPerCar: '-',
              };
            }),
          };
          return { parentRow, rows };
        });
    };

    return {
      data: getTableData,
      headers,
      paginationInfo,
      scrolling: true,
      isLoading: isFetching,
      fetchSortedData: triggerUtilities,
      subRowInfo: {
        subRows: getSubRows(),
        displayProperty: 'name',
        identifier: '_id',
      },
    };
  }, [
    intl,
    hasAnyActionPermission,
    paginationData,
    getTableData,
    triggerUtilities,
    isFetching,
    utilities,
    initializeActionButtonHeader,
  ]);

  return (
    <div className="purchase-orders">
      <UserGroupPermissions itemRef={viewPermissionRefs.utility_chart_section}>
        <div className="utilities-graph-section">
          {utilityVisualIds[String(pageFilter)?.toLowerCase()]?.map(({ id, title }) => (
            <div key={id}>
              <VisualComponent
                visualId={id}
                pageName="ReportSectionde220715d8729a0eb06d"
                title={title}
                selectorFn={selectDashboard}
                dashboardType="utility"
              />
            </div>
          ))}
        </div>
      </UserGroupPermissions>
      <div
        className="filters-actions-container"
        style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}
      >
        <PageActions
          actionFunction={(type, props) => dispatch(showDialog(type, props))}
          actions={pageActions}
        />
      </div>
      <div className="table-buttons"></div>
      <DataTable {...initializeDatatable} />
    </div>
  );
};

UtilityTable.propTypes = {
  pageFilter: PropTypes.string,
};

export default UtilityTable;
